import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RightPanel from '../../../components/RightPanel';
import { casesApi, commentApi, projectsApi, teamApi } from '../../../services/api';
import RichCommentBox from '../../../components/RichCommentBox';
import { ProjectCase, Comment, User, CaseStatusHistory, ProjectListItem } from '../../../types';
import { showSuccess, showError } from '../../../utils/toast';
import { X, Heart, MessageSquare, Clock, Edit2 } from 'lucide-react';
import Skeleton from '../../../components/Skeleton';
import Badge from '../../../components/ui/Badge';
import { caseStatusVariant, casePriorityVariant } from '../../../utils/badgeVariants';

interface CaseSidePanelProps {
  caseId: number;
  onClose: () => void;
  onUpdate?: () => void;
  mode?: 'view' | 'edit' | 'comments';
  onModeChange?: (newMode: 'view' | 'edit' | 'comments') => void;
}

const CaseSidePanel: React.FC<CaseSidePanelProps> = ({ caseId, onClose, onUpdate, mode = 'comments', onModeChange }) => {
  const [caseData, setCaseData] = useState<ProjectCase | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [statusHistory, setStatusHistory] = useState<CaseStatusHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'updates' | 'history'>('details');
  const [liking, setLiking] = useState<Record<number, boolean>>({});
  const [statusChangeNotes, setStatusChangeNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    case_subject: '',
    project_id: null as number | null,
    case_priority: 'medium' as string,
    case_status: 'ready' as string,
    assigned_team_id: null as number | null,
    type: '',
    due_date: '',
    description: '',
  });
  const [projects, setProjects] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);

  useEffect(() => {
    setEditMode(mode === 'edit');
  }, [mode]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await casesApi.getById(caseId);
        setCaseData(data);
        if (data.statusHistory) {
          setStatusHistory(data.statusHistory);
        }
      } catch (error) {
        console.error('Failed to load case:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId]);

  useEffect(() => {
    if (caseData) {
      setForm({
        case_subject: caseData.case_subject || '',
        project_id: caseData.project_id || null,
        case_priority: caseData.case_priority || 'medium',
        case_status: caseData.case_status || 'ready',
        assigned_team_id: caseData.assigned_team_id || null,
        type: caseData.type || '',
        due_date: caseData.due_date ? caseData.due_date.split('T')[0] : '',
        description: caseData.description || '',
      });
    }
  }, [caseData]);

  useEffect(() => {
    if (mode === 'comments') {
      const loadComments = async () => {
        setLoadingComments(true);
        try {
          const res = await commentApi.getAllForCase(caseId, null);
          const rows = (res.data || []).slice().sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          setComments(rows);
        } catch (error) {
          console.error('Failed to load comments:', error);
        } finally {
          setLoadingComments(false);
        }
      };
      loadComments();
    }
  }, [caseId, mode]);
  
  useEffect(() => {
    const loadProjectsAndTeams = async () => {
      try {
        const [projectsData, teamsData] = await Promise.all([
          projectsApi.list({}),
          teamApi.list({ per_page: 1000 }),
        ]);
        setProjects(projectsData.data || []);
        setTeams(teamsData.data || []);
      } catch (error) {
        console.error('Failed to load projects/teams:', error);
      }
    };
    if (editMode) {
      loadProjectsAndTeams();
    }
  }, [editMode]);

  const handleStatusChange = async (newStatus: string) => {
    if (!caseData || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      await casesApi.updateStatus(caseData.id, newStatus, statusChangeNotes);
      const updated = await casesApi.getById(caseId);
      setCaseData(updated);
      if (updated.statusHistory) {
        setStatusHistory(updated.statusHistory);
      }
      setStatusChangeNotes('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleNewComment = (created?: Comment) => {
    if (created) {
      setComments((prev) => [{ ...created }, ...prev]);
    } else {
      // Reload comments
      const loadComments = async () => {
        const res = await commentApi.getAllForCase(caseId, null);
        const rows = (res.data || []).slice().sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setComments(rows);
      };
      loadComments();
    }
  };

  const toggleLike = async (comment: Comment) => {
    if (liking[comment.id]) return;
    setLiking((prev) => ({ ...prev, [comment.id]: true }));
    const optimisticLiked = !comment.is_liked;
    const prevCount = comment.likes_count || 0;
    setComments((prev) =>
      prev.map((c) =>
        c.id === comment.id
          ? { ...c, is_liked: optimisticLiked, likes_count: Math.max(0, prevCount + (optimisticLiked ? 1 : -1)) }
          : c
      )
    );
    try {
      const res = optimisticLiked
        ? await commentApi.like(comment.id)
        : await commentApi.unlike(comment.id);
      setComments((prev) =>
        prev.map((c) => (c.id === comment.id ? { ...c, is_liked: res.is_liked, likes_count: res.likes_count } : c))
      );
    } catch (e) {
      setComments((prev) =>
        prev.map((c) => (c.id === comment.id ? { ...c, is_liked: !optimisticLiked, likes_count: prevCount } : c))
      );
    } finally {
      setLiking((prev) => ({ ...prev, [comment.id]: false }));
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'project_id' || name === 'assigned_team_id' ? (value ? Number(value) : null) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    
    try {
      await casesApi.update(caseId, {
        case_subject: form.case_subject,
        project_id: form.project_id,
        case_priority: form.case_priority,
        case_status: form.case_status,
        assigned_team_id: form.assigned_team_id,
        type: form.type,
        due_date: form.due_date || null,
        description: form.description || null,
      });
      
      // Reload case data
      const updatedCase = await casesApi.getById(caseId);
      setCaseData(updatedCase);
      if (updatedCase.statusHistory) {
        setStatusHistory(updatedCase.statusHistory);
      }
      
      showSuccess('Case updated successfully!');
      setEditMode(false);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Update failed';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (caseData) {
      setForm({
        case_subject: caseData.case_subject || '',
        project_id: caseData.project_id || null,
        case_priority: caseData.case_priority || 'medium',
        case_status: caseData.case_status || 'ready',
        assigned_team_id: caseData.assigned_team_id || null,
        type: caseData.type || '',
        due_date: caseData.due_date ? caseData.due_date.split('T')[0] : '',
        description: caseData.description || '',
      });
    }
    setEditMode(false);
  };

  const header = (
    <div className="min-w-0">
      <p className="text-xs theme-text-secondary font-mono truncate">
        {caseData?.case_number || `CASE-${caseId}`}
      </p>
      <h3 className="text-base font-semibold theme-text-primary truncate">
        {caseData?.case_subject || 'Loading…'}
      </h3>
    </div>
  );

  const headerExtras = mode === 'view' ? (
    <button
      onClick={() => {
        if (onModeChange) {
          onModeChange('edit');
        } else {
          setEditMode(true);
        }
      }}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
      title="Edit case"
    >
      <Edit2 className="w-3.5 h-3.5" />
      Edit
    </button>
  ) : undefined;

  return (
    <RightPanel isOpen={true} onClose={onClose} title={header} headerExtras={headerExtras}>
      {loading ? (
        <div className="space-y-3 p-1">
          <Skeleton height={60} variant="rounded" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton height={48} variant="rounded" />
            <Skeleton height={48} variant="rounded" />
          </div>
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
        </div>
      ) : caseData ? (
        <>
          {mode === 'view' && (
            <div className="space-y-3">
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-1">Description</h4>
                <p className="text-sm theme-text-primary leading-relaxed">
                  {caseData.description || 'No description'}
                </p>
              </div>
              
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-2">Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="theme-text-secondary">Status:</span>
                    <Badge variant={caseStatusVariant(caseData.case_status)} className="ml-2">
                      {caseData.case_status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Priority:</span>
                    <Badge variant={casePriorityVariant(caseData.case_priority)} className="ml-2">
                      {caseData.case_priority}
                    </Badge>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Assigned Team:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {caseData.assignedTeam?.name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Project:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {caseData.project?.name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Type:</span>
                    <span className="ml-2 font-medium theme-text-primary">{caseData.type || '-'}</span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Due Date:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {caseData.due_date ? new Date(caseData.due_date).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="theme-text-secondary">Created:</span>
                    <span className="ml-2 font-medium theme-text-primary">
                      {caseData.created_at ? new Date(caseData.created_at).toLocaleDateString() : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === 'edit' && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">Case Subject</label>
                <input
                  type="text"
                  name="case_subject"
                  value={form.case_subject}
                  onChange={handleFormChange}
                  className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium theme-text-primary mb-1">Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  rows={4}
                  className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Project</label>
                  <select
                    name="project_id"
                    value={form.project_id || ''}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  >
                    <option value="">No project</option>
                    {projects.map((p: ProjectListItem) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Type</label>
                  <input
                    type="text"
                    name="type"
                    value={form.type}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Status</label>
                  <select
                    name="case_status"
                    value={form.case_status}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  >
                    <option value="ready">Ready</option>
                    <option value="working">Working</option>
                    <option value="waiting">Waiting</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="approved">Approved</option>
                    <option value="uploading">Uploading</option>
                    <option value="closed">Closed</option>
                    <option value="additional_request">Additional Request</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Priority</label>
                  <select
                    name="case_priority"
                    value={form.case_priority}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Assigned Team</label>
                  <select
                    name="assigned_team_id"
                    value={form.assigned_team_id || ''}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  >
                    <option value="">No team</option>
                    {teams.map((t: any) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-1">Due Date</label>
                  <input
                    type="date"
                    name="due_date"
                    value={form.due_date}
                    onChange={handleFormChange}
                    className="w-full theme-border rounded px-2 py-1.5 text-sm theme-bg-primary theme-text-primary"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}

          {mode === 'comments' && (
            <>
              {/* Tabs */}
              <div className="mt-3">
                <div className="flex items-center gap-2 border-b border-gray-200">
                  <button
                    className={`px-3 py-2 text-sm font-medium ${
                      activeTab === 'details'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab('details')}
                  >
                    Details
                  </button>
                  <button
                    className={`px-3 py-2 text-sm font-medium ${
                      activeTab === 'updates'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab('updates')}
                  >
                    Updates
                  </button>
                  <button
                    className={`px-3 py-2 text-sm font-medium ${
                      activeTab === 'history'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                    onClick={() => setActiveTab('history')}
                  >
                    History
                  </button>
                </div>
              </div>

              {activeTab === 'details' && (
            <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Description</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {caseData.description || 'No description'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Status</h4>
                  <select
                    value={caseData.case_status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updatingStatus}
                    className={`w-full px-2 py-1 text-sm rounded border ${getStatusColor(caseData.case_status)}`}
                  >
                    <option value="ready">Ready</option>
                    <option value="working">Working</option>
                    <option value="waiting">Waiting</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="approved">Approved</option>
                    <option value="uploading">Uploading</option>
                    <option value="closed">Closed</option>
                    <option value="additional_request">Additional Request</option>
                  </select>
                </div>

                <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                  <h4 className="text-xs font-semibold text-gray-600 mb-1">Priority</h4>
                  <Badge variant={casePriorityVariant(caseData.case_priority)}>
                    {caseData.case_priority}
                  </Badge>
                </div>
              </div>

              <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                <h4 className="text-xs font-semibold text-gray-600 mb-1">Project</h4>
                <p className="text-sm text-gray-800">{caseData.project?.name || 'No project'}</p>
              </div>

              <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                <h4 className="text-xs font-semibold text-gray-600 mb-1">Assigned Team</h4>
                <p className="text-sm text-gray-800">{caseData.assignedTeam?.name || 'No team'}</p>
              </div>

              <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                <h4 className="text-xs font-semibold text-gray-600 mb-1">Due Date</h4>
                <p className="text-sm text-gray-800">
                  {caseData.due_date ? new Date(caseData.due_date).toLocaleDateString() : 'No due date'}
                </p>
              </div>

              <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                <h4 className="text-xs font-semibold text-gray-600 mb-1">Type</h4>
                <p className="text-sm text-gray-800">{caseData.type}</p>
              </div>
            </div>
              )}

              {activeTab === 'updates' && (
            <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-white/30 bg-white/70 p-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Comments & Mentions</h4>
                <RichCommentBox caseId={caseId} onCommentCreated={handleNewComment} />

                <div className="mt-4 space-y-3">
                  {loadingComments && <div className="text-xs text-gray-500">Loading comments…</div>}
                  {comments.map((c) => (
                    <div key={c.id} className="rounded-lg border border-white/30 bg-white p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                          {(c.author?.name || 'U')
                            .split(' ')
                            .map((w) => w[0])
                            .slice(0, 2)
                            .join('')}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {c.author?.name || 'Unknown'}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {new Date(c.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div
                        className="prose prose-sm max-w-none text-gray-800 mb-2"
                        dangerouslySetInnerHTML={{ __html: c.html }}
                      />

                      <div className="flex items-center gap-4 text-xs">
                        <button
                          onClick={() => toggleLike(c)}
                          disabled={!!liking[c.id]}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${
                            c.is_liked
                              ? 'bg-rose-50 border-rose-200 text-rose-600'
                              : 'bg-white border-gray-200 text-gray-600'
                          } hover:bg-gray-50`}
                        >
                          <Heart className={`w-3.5 h-3.5 ${c.is_liked ? 'fill-rose-500 stroke-rose-500' : ''}`} />
                          <span>{c.likes_count ?? 0}</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
              )}

              {activeTab === 'history' && (
            <div className="mt-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Status Change History</h4>
              {statusHistory.length === 0 ? (
                <div className="text-sm text-gray-500">No status changes yet</div>
              ) : (
                statusHistory.map((h) => (
                  <div key={h.id} className="rounded-lg border border-white/30 bg-white/70 p-3">
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {h.old_status ? (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <Badge variant={caseStatusVariant(h.old_status)}>
                                {h.old_status.replace('_', ' ')}
                              </Badge>
                              {' → '}
                              <Badge variant={caseStatusVariant(h.new_status)}>
                                {h.new_status.replace('_', ' ')}
                              </Badge>
                            </span>
                          ) : (
                            <Badge variant={caseStatusVariant(h.new_status)}>
                              {h.new_status.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          Changed by {h.changedBy?.name || 'Unknown'} on{' '}
                          {new Date(h.created_at).toLocaleString()}
                        </p>
                        {h.notes && (
                          <p className="text-xs text-gray-600 mt-1 italic">{h.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="text-sm text-gray-500">Case not found</div>
      )}
    </RightPanel>
  );
};

export default CaseSidePanel;

